import { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { 
  Search, MapPin, Clock, Tag, Users, Heart, ShoppingCart, User,
  Utensils, Briefcase, ShoppingBag, Home, Gamepad2, Car, Flame, Gift
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

// Category config
const categoryConfig = [
  { name: "I più venduti", icon: Flame, key: "all" },
  { name: "Ristorazione", icon: Utensils, key: "Ristorazione" },
  { name: "Shopping", icon: ShoppingBag, key: "Shopping" },
  { name: "Benessere", icon: Heart, key: "Salute e Benessere" },
  { name: "Servizi", icon: Briefcase, key: "Servizi" },
  { name: "Casa", icon: Home, key: "Casa e Giardino" },
  { name: "Tempo Libero", icon: Gamepad2, key: "Tempo Libero" },
  { name: "Auto", icon: Car, key: "Auto e Moto" },
];

const categoryColors = {
  "Ristorazione": "from-orange-500 to-red-500",
  "Servizi": "from-blue-500 to-indigo-500",
  "Shopping": "from-pink-500 to-rose-500",
  "Salute e Benessere": "from-emerald-500 to-teal-500",
  "Casa e Giardino": "from-lime-500 to-green-500",
  "Tempo Libero": "from-purple-500 to-violet-500",
  "Auto e Moto": "from-gray-600 to-slate-700",
};

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userPointValue, setUserPointValue] = useState(0.07);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [offersRes, citiesRes, pointsRes] = await Promise.all([
        api.get('/offers'),
        api.get('/cities'),
        api.get('/settings/points').catch(() => ({ data: { user_point_value: 0.07 } })),
      ]);
      setOffers(offersRes.data);
      setCities(citiesRes.data.cities || []);
      setUserPointValue(pointsRes.data.user_point_value || 0.07);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesCategory = selectedCategory === 'all' || offer.category === selectedCategory;
    const matchesCity = !selectedCity || 
                        (offer.company_city && offer.company_city.toLowerCase().includes(selectedCity.toLowerCase()));
    const matchesSearch = !searchQuery || 
                         offer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         offer.company_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesCity && matchesSearch;
  });

  const daysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header - Groupon Style */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          {/* Top row: logo + actions */}
          <div className="flex items-center justify-between h-24 sm:h-28 gap-3 sm:gap-4">
            {/* Logo */}
            <Logo linkTo="/" size="md" />

            {/* Search Bar - Central (desktop only) */}
            <div className="hidden md:flex flex-1 max-w-2xl">
              <div className="relative flex items-center w-full">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Cerca proposte, ristoranti, servizi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 h-11 rounded-l-full rounded-r-none border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    data-testid="search-input"
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="h-11 pl-9 pr-8 border border-l-0 border-gray-300 bg-white text-sm focus:outline-none focus:border-emerald-500 appearance-none cursor-pointer"
                    data-testid="city-select"
                  >
                    <option value="">Tutte le città</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <Button className="h-11 px-6 rounded-l-none rounded-r-full bg-emerald-600 hover:bg-emerald-700">
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <Link to="/come-funziona" className="hidden md:inline-flex">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-emerald-600" data-testid="how-it-works-link">
                  Come funziona
                </Button>
              </Link>
              <Link to="/login" className="hidden sm:inline-flex">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-emerald-600">
                  <Heart className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login" className="hidden sm:inline-flex">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-emerald-600">
                  <ShoppingCart className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                  <User className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Accedi</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile search row */}
          <div className="md:hidden pb-3 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca proposte..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 rounded-full border-gray-300 focus:border-emerald-500"
                data-testid="search-input-mobile"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="h-10 pl-8 pr-3 border border-gray-300 rounded-full bg-white text-sm focus:outline-none focus:border-emerald-500 appearance-none max-w-[130px]"
                data-testid="city-select-mobile"
              >
                <option value="">Città</option>
                {cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="border-t border-gray-100 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {categoryConfig.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-emerald-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`category-${cat.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {selectedCategory === 'all' ? 'Tutte le proposte' : selectedCategory}
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              {filteredOffers.length} proposte disponibili
              {selectedCity && ` a ${selectedCity}`}
            </p>
          </div>
          {(selectedCity || searchQuery || selectedCategory !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSelectedCategory('all'); setSelectedCity(''); setSearchQuery(''); }}
              className="text-emerald-600 shrink-0"
            >
              Rimuovi filtri
            </Button>
          )}
        </div>

        {/* Offers Grid */}
        {filteredOffers.length === 0 ? (
          <div className="text-center py-20">
            <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna proposta trovata</h3>
            <p className="text-gray-500 mb-4">Prova a modificare i filtri di ricerca</p>
            <Button 
              variant="outline" 
              onClick={() => { setSelectedCategory('all'); setSelectedCity(''); setSearchQuery(''); }}
            >
              Mostra tutte le proposte
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
            {filteredOffers.map((offer) => {
              const days = daysRemaining(offer.end_date);
              const gradientClass = categoryColors[offer.category] || "from-gray-500 to-gray-600";
              const available = offer.max_uses ? offer.max_uses - offer.current_uses : null;
              
              return (
                <Link key={offer.id} to={`/offers/${offer.id}`}>
                  <Card 
                    className="group overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-200 h-full"
                    data-testid={`offer-card-${offer.id}`}
                  >
                    {/* Image Area */}
                    <div className={`h-32 sm:h-40 relative ${offer.image_url ? '' : `bg-gradient-to-br ${gradientClass}`}`}>
                      {offer.image_url && (
                        <img
                          src={offer.image_url}
                          alt={offer.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                      {/* Points Badge */}
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-emerald-600 text-white font-bold text-xs sm:text-sm px-2 sm:px-3 py-1 shadow-md">
                          {Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} punti / €{Math.round(offer.base_price ?? 100)}
                        </Badge>
                      </div>
                      
                      {/* Urgency */}
                      {days <= 3 && days > 0 && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-red-500 text-white text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {days}g rimasti
                          </Badge>
                        </div>
                      )}

                      {/* Favorite Button */}
                      <button className="absolute bottom-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Heart className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                    
                    <CardContent className="p-3 sm:p-4">
                      {/* Title */}
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 text-sm sm:text-base group-hover:text-emerald-600 transition-colors">
                        {offer.title}
                      </h3>
                      
                      {/* Company */}
                      <p className="text-xs sm:text-sm text-gray-500 mb-2 truncate">{offer.company_name}</p>
                      
                      {/* Location */}
                      {offer.company_city && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mb-2 sm:mb-3">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{offer.company_city}</span>
                        </div>
                      )}
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100 gap-2">
                        <div className="min-w-0">
                          <span className="block text-[10px] sm:text-xs text-gray-400 truncate">
                            Scade il {new Date(offer.end_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                        {available !== null && (
                          <span className="text-[10px] sm:text-xs text-emerald-600 font-medium shrink-0">
                            {available} disp.
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            © 2024 AIAC Card - Associazione Italiana Amministratori di Condominio
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Offers;
